import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Share, PlusSquare, X, Download, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LOGO, APP_NAME } from "@/lib/brand";
import { t } from "@/lib/i18n";

const KEY = "install_prompt_snooze_until";
const SNOOZE_DAYS = 7;

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.matchMedia("(display-mode: fullscreen)").matches ||
  window.navigator.standalone === true;
const ua = () => navigator.userAgent || "";
const isIOS = () => /iPhone|iPad|iPod/i.test(ua()) || (/Macintosh/.test(ua()) && navigator.maxTouchPoints > 1);
const isMobile = () => isIOS() || /Android|Mobile/i.test(ua());
const snoozed = () => Number(localStorage.getItem(KEY) || 0) > Date.now();

function Steps({ ios }) {
  const Step = ({ n, children }) => (
    <li className="flex items-center gap-2"><span className="h-5 w-5 rounded-full bg-primary/15 text-primary text-[11px] font-bold grid place-items-center shrink-0">{n}</span>{children}</li>
  );
  return (
    <ol className="mt-3 space-y-2 text-sm" data-testid="install-steps">
      {ios ? (
        <>
          <Step n="1"><span>{t("Tocca")}</span><Share className="h-4 w-4 text-primary" /><b>{t("Condividi")}</b></Step>
          <Step n="2"><span>{t("Scegli")}</span><PlusSquare className="h-4 w-4 text-primary" /><b>{t("Aggiungi a Home")}</b></Step>
        </>
      ) : (
        <>
          <Step n="1"><span>{t("Apri il menu del browser")}</span><MoreVertical className="h-4 w-4 text-primary" /></Step>
          <Step n="2"><b>{t("Installa app")}</b><span>/</span><b>{t("Aggiungi a schermata Home")}</b></Step>
        </>
      )}
    </ol>
  );
}

export function InstallPrompt() {
  const [deferred, setDeferred] = useState(() => window.__ordexInstallPrompt || null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isMobile() || isStandalone() || snoozed()) return;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => setOpen(false);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    const timer = setTimeout(() => setOpen(true), 1500);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(KEY, String(Date.now() + SNOOZE_DAYS * 86400000));
    setOpen(false);
  };
  const install = async () => {
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null);
    window.__ordexInstallPrompt = null;
    if (outcome === "accepted") setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ y: 120, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 120, opacity: 0 }} transition={{ type: "spring", damping: 24 }}
          className="fixed inset-x-3 bottom-3 z-50 rounded-2xl bg-white border border-border shadow-2xl p-4" data-testid="install-prompt">
          <button onClick={dismiss} className="absolute top-3 right-3 p-1 rounded-md text-muted-foreground hover:bg-muted" data-testid="install-close-btn" aria-label={t("Chiudi")}>
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-3 pr-6">
            <img src={LOGO} alt={APP_NAME} className="h-11 w-11 rounded-xl shadow-sm" />
            <div className="min-w-0">
              <p className="font-semibold text-sm">{t("Installa ORDEXA sul telefono")}</p>
              <p className="text-xs text-muted-foreground">{t("Si apre a schermo intero, come un'app, direttamente dalla schermata Home.")}</p>
            </div>
          </div>
          {deferred ? null : <Steps ios={isIOS()} />}
          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="flex-1" onClick={dismiss} data-testid="install-later-btn">{t("Non ora")}</Button>
            {deferred && (
              <Button className="flex-1 gap-2 font-semibold" onClick={install} data-testid="install-confirm-btn"><Download className="h-4 w-4" /> {t("Installa")}</Button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
